import pluralize from "pluralize";
import toast from "react-hot-toast";

import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {ANALYZER_TOAST_DURATION_MS} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerSetLocationsFormValues, AnalyzerSetLocationsModalProps} from "@/pages/analyzer/Analyzer.types.ts";
import {getBulkLocationsBound, getLocationsForProposal} from "@/pages/analyzer/Analyzer.utils.ts";
import {AnalyzerSetLocationsForm} from "@/pages/analyzer/components/AnalyzerSetLocationsForm.tsx";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";

const FORM_ID = "analyzer-set-locations-form";

export const AnalyzerSetLocationsModal = ({show, setShow}: AnalyzerSetLocationsModalProps) => {
  const {selectedProposals, updateProposedChecks, formData} = useAnalyzerContext();
  const defaultNumLocations = Math.max(1, ...selectedProposals.map(({num_locations}) => num_locations ?? 0));
  const maxNumLocations = formData ? getBulkLocationsBound(formData, selectedProposals) : undefined;

  const handleSubmit = ({num_locations}: AnalyzerSetLocationsFormValues) => {
    updateProposedChecks({num_locations});
    if (formData) {
      const effective = selectedProposals.map((check) => ({
        check,
        numLocations: Math.min(num_locations, getLocationsForProposal(formData, {...check, num_locations}).length),
      }));
      const skipped = effective.filter(({numLocations}) => numLocations === 0).length;
      const capped = effective.filter(({numLocations}) => numLocations > 0 && numLocations < num_locations).length;
      const sensitivityReduced = effective.filter(
        ({check, numLocations}) => Number(check.msp_sensitivity) > numLocations
      ).length;
      const cappedNote = capped ? ` Capped ${pluralize("Check", capped, true)} to fewer available Locations.` : "";
      const skippedNote = skipped
        ? ` Skipped ${pluralize("Check", skipped, true)} without Locations for their type.`
        : "";
      const sensitivityNote = sensitivityReduced
        ? ` Reduced Sensitivity to match on ${pluralize("Check", sensitivityReduced, true)}.`
        : "";
      toast.success(
        `Locations set to ${num_locations} on ${pluralize("Check", selectedProposals.length - capped - skipped, true)}.${cappedNote}${skippedNote}${sensitivityNote}`,
        {duration: ANALYZER_TOAST_DURATION_MS}
      );
    } else {
      toast.success("Locations updated.", {duration: ANALYZER_TOAST_DURATION_MS});
    }
    setShow(false);
  };

  return (
    <FormModal
      formId={FORM_ID}
      onCancel={() => setShow(false)}
      show={show}
      title={`Set Locations on ${pluralize("Check", selectedProposals.length, true)}`}
    >
      <AnalyzerSetLocationsForm
        formId={FORM_ID}
        onSubmit={handleSubmit}
        defaultNumLocations={defaultNumLocations}
        maxNumLocations={maxNumLocations}
      />
    </FormModal>
  );
};
